import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { debounce, isEmpty, omitBy } from 'lodash';
import {
  CBadge,
  CButton,
  CCard,
  CCardHeader,
  CCol,
  CCollapse,
  CDataTable,
  CFormGroup,
  CImg,
  CInputCheckbox,
  CRow,
  CTooltip,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CPagination
} from '@coreui/react';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import * as pluralize from 'pluralize';
import { useFlags } from 'launchdarkly-react-client-sdk';
import tooltip from '../../assets/images/tool_tip.svg';
import { RootState } from '../../store/state';
import { Job, JobPhase, JobPhaseColor, JobPhaseFilterMapper, JobPhaseLabel, TransformedJob } from '../../shared/models/jobs';
import { CancelJob, CompleteJob, ExportRecords, SearchJobs, StartSearchJobsPolling, StopSearchJobsPolling } from '../actions';
import { LoadUsers } from '../../technicians/actions/userManagement.action';
import { UserInfo } from '../../shared/models/user';
import CreateJobModal from './modals/AddJobModal';
import JobActions from '../components/JobActions';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import TableStats from '../../shared/components/TableStats';
import { selectTechniciansIncludingRemoved } from '../../store/selectors/user.selectors';
import JobsDetailsContainer from '../../dashboard/components/JobsDetailsContainer';
import FeedbackRating from '../../shared/components/FeedbackRating';
import { OpenModal } from '../../store/actions/modal.action';
import { ConfirmationDataBag, ModalName, ReassignJobDataBag } from '../../shared/models/modal';
import { selectOrgHierarchyRootOrg, selectOrgHierarchySelectedOrg, selectOrgList } from '../../store/selectors/orgHierarchy.selectors';
import OrgHierarchyFilter from '../../shared/components/orgHierarchy/OrgHierarchyFilter';
import { formatJobDescription } from '../../shared/utils/job-utils';

export const PAGE_SIZE = 10;

interface BulkActionState {
  action: string;
  selected: Job[];
}

export interface JobsViewProps extends DispatchProp {
  search: Job[];
  users: UserInfo[];
  jobsLoading: boolean;
  usersLoading: boolean;
  totalTasks: number;
}

const BULK_ASSIGN_ACTION = {
    label: 'Assign / Reassign',
    action: (dispatch, jobs: Job[]) => dispatch(new OpenModal(ModalName.ReassignJob, { jobs } as ReassignJobDataBag)),
  }
const BULK_EXPORT_ACTION = labels => ({
  label: 'Export',
  action: (dispatch, jobs: Job[]) => dispatch(new OpenModal(ModalName.Confirmation, {
    title: `Export ${pluralize(labels.task, jobs.length, true)}`,
    message: `Are you sure you want to export ${pluralize(labels.task, jobs.length, true).toLowerCase()}?`,
    confirmAction: () => dispatch(new ExportRecords(jobs.map(job => job._id))),
  } as ConfirmationDataBag)),
});

const BULK_JOBS_ACTIONS = labels => [
  {
    label: 'Cancel',
    action: (dispatch, jobs: Job[]) => dispatch(new OpenModal(ModalName.Confirmation, {
        title: `Cancel ${pluralize(labels.task, jobs.length, true)}`,
        message: `Are you sure you want to cancel ${pluralize(labels.task, jobs.length, true).toLowerCase()}?`,
        confirmAction: () => jobs.map(job => dispatch(new CancelJob(job._id))),
      } as ConfirmationDataBag)),
  },
  {
    label: 'Mark Completed',
    action: (dispatch, jobs: Job[]) => dispatch(new OpenModal(ModalName.Confirmation, {
      title: `Mark ${pluralize(labels.task, jobs.length, true)} Completed`,
      message: `Are you sure you want to mark ${pluralize(labels.task, jobs.length, true).toLowerCase()} completed?`,
      confirmAction: () => jobs.map(job => dispatch(new CompleteJob(job._id))),
    } as ConfirmationDataBag)),
  }
];

const BULK_ACTIONS = labels => [
  BULK_ASSIGN_ACTION,
  ...BULK_JOBS_ACTIONS(labels),
  BULK_EXPORT_ACTION(labels),
];

const JobsListPage = (props: JobsViewProps) => {
  const { 
    search,
    totalTasks, 
    users, 
    jobsLoading, 
    usersLoading, 
    dispatch 
  } = props;
  const {
    enableViewOnlyMode,
    enableAssignJobsMode,
    enableOrgHierarchyModule,
    enableBulkUploadModule,
  } = useFlags();

  const tableRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<BulkActionState>({ action: null, selected: [] });
  const history = useHistory();
  const { query } = useParams<{query: string}>();
  const labels = useContext(ConfigContext);
  const [showDrawer, setShowDrawer] = useState<number>(undefined);
  const [filteredJobs, setFilteredJobs] = useState(null);
  const [filters, setFilters] = useState(null);
  const [sortOptions, setSortOptions] = useState<{sort_direction?: string, sort_field?: string}>({
    sort_direction: "desc",
    sort_field: "due_time"
  });

  const rootOrg = useSelector(selectOrgHierarchyRootOrg);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);
  const rootOrgID = rootOrg?._id;
  const selectedOrgID = selectedOrg?._id;
  const orgs = useSelector(selectOrgList);
  const [selectedOrgs, setSelectedOrgs] = useState([]);

  const showFilter = search && search.length > 0 || !isEmpty(filters);

  const selectableJobs = search?.filter(job => [JobPhase.Scheduled, JobPhase.Pre, JobPhase.Live].includes(job.phase));
  const isSessionsEnabled = selectedOrg?.config?.sessions?.enabled;

  /* eslint-disable react/prop-types */
  const FIELDS = ({agent, task}) => [
    ...(!enableViewOnlyMode ? [{
      key: 'select',
      label: <div>
        <CFormGroup variant="custom-checkbox">
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label,jsx-a11y/label-has-associated-control */}
          <label htmlFor='select-all'>
            <CInputCheckbox
              id="select-all"
              name="select-all"
              checked={ selectableJobs?.length === bulkAction.selected.length && selectableJobs.length !== 0 }
              onChange={ () => {
                if (bulkAction.selected.length === selectableJobs.length) {
                  setBulkAction({ action: bulkAction.action, selected: [] });
                } else {
                  setBulkAction({ action: bulkAction.action, selected: selectableJobs })
                }
              } }
            />
            <CTooltip placement="top" content="Select All">
              <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
            </CTooltip>
          </label>
        </CFormGroup>
      </div>,
      sorter: false,
      filter: false,
    }] : []),
    {
      key: 'appointment_id',
      label: <div>{ task } #
        <CTooltip placement="top" content="Customer’s identifier for Delivery/Appointment">
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
        </CTooltip>
      </div>,
      filter: showFilter,
      sorter: true,
      _classes: "appointment-id-column"
    },
    {
      key: 'agent',
      label: <div>{ agent }
        <CTooltip placement="top" content={`Name of the ${agent.toLowerCase()} assigned to the ${task.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
        </CTooltip>
      </div>,
      sorter: false,
      filter: showFilter,
    },
    {
      key: 'description',
      label: <div>Description
        <CTooltip placement="top" content={`Non-customer-facing field to give detail about ${task.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
        </CTooltip>
      </div>,
      sorter: false,
      filter: showFilter,
    },
    {
      key: 'destination',
      label: <div>Destination
        <CTooltip placement="top" content={`${task} address.`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
        </CTooltip>
      </div>,
      sorter: false,
      filter: showFilter,
    },
    {
      key: 'phase',
      label: <div>Status
        <CTooltip placement="top" content={`Current status of ${task.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableStatus"/>
        </CTooltip>
      </div>,
      sorter: true,
      filter: showFilter,
    },
    {
      key: 'due_time',
      label: <>Promised Time
        <CTooltip placement="top" content={`Expected time of ${task.toLowerCase()}`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
        </CTooltip>
      </>,
      filter: false,
      _classes: ["due-time-header"]
    },
    ...(enableOrgHierarchyModule ? [{
      key: 'orgName',
      label: 'Org Name',
      filter: false,
      sorter: false,
    }] : []),
    ...(isSessionsEnabled ? [{
      key: 'session_id',
      label: 'Session ID',
    }, {
      key: 'rank',
      label: 'Session Rank',
      sorter: false,
      filter: false,
    }] : []),
    {
      key: 'actions',
      label: '',
      sorter: false,
      filter: false,
    },
  ];

  const scrollToLeft = () => {
    tableRef?.current?.querySelector('.position-relative')?.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  };

  const handleRowClick = (job: TransformedJob, index, columnName, e) => {
    if (!['actions', 'select'].includes(columnName) || e.target.tagName.toLowerCase() === 'td') {
      const isOpenDetails = showDrawer !== job?.id;
      setShowDrawer(isOpenDetails ? job?.id : undefined);
      if (isOpenDetails) {
        scrollToLeft();
      }
    }
  };

  const transformJobs = (data) => {
    return data.map((job: Job) => ({
        id: job._id,
        appointment_id: job.metadata?.find(obj => obj.n === 'appointment_id')?.v || job?.foreign_id,
        agent: users?.find(user => user.id === job.agent_id)?.displayName || 'Unassigned',
        description: formatJobDescription(job.description, job?.foreign_id, isSessionsEnabled),
        destination: job.destination.name,
        phase: JobPhaseLabel[job.phase] || 'Unknown',
        due_time: job.due_time > 0 ? moment(job.due_time).format('l LT') : '-',
        recipients: job.recipients?.map((recipient) => recipient.address),
        orgId: job.org_id,
        orgName: orgs?.find(value => value?._id === job?.org_id)?.name ?? '',
        job,
        session_id: job.session_id ?? '-',
        rank: job.rank >= 0 ? job.rank : '-',
        completed: job.completed,
        completedReasonCode: job.metadata?.find(obj => obj.n === 'completed_reasoncode')?.v
      } as TransformedJob
    ))?.sort((a, b) => {
      if (!Number.isInteger(a.session_id) || !Number.isInteger(b.session_id)) {
        return 0;
      }
      if (a.session_id === b.session_id) {
        return a.rank > b.rank ? 1 : -1;
      }
      return 0;
    });
  };

  const filterFieldMap = {
    description: 'description',
    destination: 'destination_name',
    agent: 'agent_ids',
    phase: 'phase',
    appointment_id: 'foreign_id',
    session_id: 'session_id',
  };

  const sortFieldMap = {
    description: 'description',
    destination: 'destination_name',
    agent: 'agent_id',
    phase: 'phase',
    due_time: 'due_time',
    appointment_id: 'foreign_id',
    session_id: 'session_id',
  };

  const getSearchParams = (orgId: number, queryValue: string, filter: any, sortOption: any, page: number, orgIds: number[]) => {
    
    const sortParams = sortOption ? {
      sort_field: sortFieldMap[sortOption.column] ?? 'due_time',
      sort_direction: sortOption?.asc ? 'asc' : 'desc',
    } : {};

    const filterParams = Object.keys(filter ?? {})?.reduce((acc, filterKey) => {
      if (filterKey === 'agent') {
        if (filter[filterKey]) {
          const usersFilter = users.filter((user) => user?.displayName?.toLowerCase()?.indexOf(filter[filterKey]?.toLowerCase()) > -1)?.map(i => i?.id);
          acc[filterFieldMap[filterKey]] = usersFilter?.length > 0 ? usersFilter : [0];
        }
      } else if (filterKey === 'phase') {
        const phase = Object.keys(JobPhaseFilterMapper).find((key) => key.indexOf(filter[filterKey]?.toLowerCase()) > -1);
        acc[filterFieldMap[filterKey]] = phase ? JobPhaseFilterMapper[phase] : [];
      } else {
        acc[filterFieldMap[filterKey]] = filter[filterKey];
      }
      return acc;
    }, {});

    return {
      org_ids: orgIds?.length > 0 ? orgIds : [...(orgs?.filter(org => org.hierarchy?.indexOf(selectedOrgID) > -1 || org._id === selectedOrgID)?.map(org => org?._id))],
      ...(queryValue ? {query: queryValue} : {}),
      ...filterParams,
      ...sortParams,
      size: PAGE_SIZE,
      skip_records: Math.max((page-1)*PAGE_SIZE, 0),
    }
  };

  const onColumnFilterChange = useCallback(debounce((filtersValue: any) => {
    if (!isEmpty(omitBy(filtersValue, isEmpty))) {
      dispatch(new StopSearchJobsPolling());
    }
    setFilters(omitBy(filtersValue, isEmpty));
  }, 300), []);

  const onColumnSortChange = (sortValue: any) => {
    if (!isEmpty(sortValue)) {
      dispatch(new StopSearchJobsPolling());
    }
    setSortOptions(sortValue);
  };

  const runSearch = (page: number) => {
    if (rootOrgID && selectedOrgID && users?.length > 0) {
      setCurrentPage(page);
      const orgIds = selectedOrgs.map(org=>org.value);
      const params = getSearchParams(selectedOrgID, query, filters, sortOptions, page, orgIds);
      if (page === 1 && isEmpty(filters) && isEmpty(sortOptions) && !query) {
        dispatch(new StartSearchJobsPolling(rootOrgID, params));
      } else {
        dispatch(new StopSearchJobsPolling());
        dispatch(new SearchJobs(rootOrgID, params));
      }
    }
  };

  const reloadJobs = () => {
    setCurrentPage(1);
    setFilters(null)
    setSortOptions(null);
    const orgIds = selectedOrgs.map(org=>org.value);
    const params = getSearchParams(selectedOrgID, query, null, null, 1, orgIds);
    dispatch(new SearchJobs(rootOrgID, params));
  }

  useEffect(() => {
    setFilters(null);
    setSortOptions(null);
    setSelectedOrgs([]);
  }, [selectedOrgID, query]);

  useEffect(() => {
    runSearch(currentPage);
    return () => {
      dispatch(new StopSearchJobsPolling());
    };
  }, [currentPage, selectedOrgID, rootOrgID]);

  useEffect(() => {
    runSearch(1);
  }, [query, filters, selectedOrgID, rootOrgID, sortOptions, users, selectedOrgs]);

  useMemo(() => {
    if (users?.length > 0 && search) {
      setFilteredJobs(transformJobs(search));
    }
  }, [search, users]);


  useEffect(() => {
    if (rootOrgID) {
      dispatch(new LoadUsers({
        orgId: selectedOrgID,
        includeChildren: enableOrgHierarchyModule
      }));
    }
  }, [rootOrgID]);

  const getBulkActions = () => {
    if (!enableViewOnlyMode) {
      return BULK_ACTIONS(labels);
    }
    return enableAssignJobsMode ? [BULK_ASSIGN_ACTION, BULK_EXPORT_ACTION(labels)] : [BULK_EXPORT_ACTION(labels)];
  }

  return (
    <>
      <CreateJobModal show={ showCreateModal } close={ () => setShowCreateModal(false) } />
      <CRow>
        <CCol lg="12">
          <CCard id="jobs-page" className="glympser-container--roundedWithShadow">
            <CCardHeader className="border-0 glympser-roundedTop">
              <div className='d-flex align-items-center justify-content-between'>
                <div className=''>{query && `Search result for '${query}'`}</div>
                <div className='card-header-actions glympser-buttonContainer'>
                  {
                    enableOrgHierarchyModule ? (
                      <div style={{display: 'inline-block', textAlign: 'left'}}>
                        <OrgHierarchyFilter
                          handleChange={(items) => {
                            setSelectedOrgs(items)
                          }}
                          options={orgs?.reduce((acc, item) => {
                            if (item.hierarchy?.indexOf(selectedOrgID) > -1 || item._id === selectedOrgID) {
                              acc.push({
                                label: item.name,
                                value: item._id
                              });
                            }
                            return acc;
                          }, []) ?? []}
                          selectedOrgs={selectedOrgs}/>
                      </div>
                    ) : null
                  }
                  {
                    !enableViewOnlyMode ? (
                      <CDropdown className="mr-2 d-inline-block">
                        <CDropdownToggle
                          color="primary"
                          className="bg-color-orange border-0 roundedBtn font-weight-xbold inline-block"
                          disabled={ !bulkAction.selected.length }
                        >
                          Mass Action
                        </CDropdownToggle>
                        <CDropdownMenu>
                          { getBulkActions().map(({ label, action }) => {
                            return <CDropdownItem key={label} onClick={ () => {
                              action(dispatch, bulkAction.selected);
                              setBulkAction({ action: null, selected: bulkAction.selected })
                            } }>{ label }</CDropdownItem>;
                          }) }
                        </CDropdownMenu>
                      </CDropdown>
                    ) : null
                  }
                  { enableBulkUploadModule && !enableViewOnlyMode && (
                    <>
                      <CButton color='info' className='mr-2 bg-color-orange border-0 roundedBtn font-weight-xbold' onClick={ () => history.push('/jobs/upload') }>Bulk Upload { pluralize(labels.task) }</CButton>
                      <CTooltip placement="top" content={`Users can upload numerous ${pluralize(labels.task.toLowerCase())} quickly. Download our example CSV for best formatting.`}>
                        <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListPageHeader"/>
                      </CTooltip>
                    </>
                  )} 
                  { !enableViewOnlyMode && <CButton color='primary' className='mr-1 bg-color-orange border-0 roundedBtn font-weight-xbold' onClick={ () => setShowCreateModal(true) }>Add {labels.task}</CButton> }
                  <CButton color='primary' className='mx-1 bg-color-orange border-0 roundedBtn font-weight-xbold' onClick={ reloadJobs}>Refresh</CButton>
                  <CTooltip placement="top" content={`Click the 'Refresh' button to ensure your ${pluralize(labels.task.toLowerCase())} list is up-to-date with the most recent information.`}>
                    <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListPageHeader"/>
                  </CTooltip>
                </div>
              </div>
            </CCardHeader>

            <div className='job-list-container' ref={tableRef}>
              <CDataTable
                columnFilterValue={filters}
                sorterValue={sortOptions}
                items={ filteredJobs }
                fields={ FIELDS(labels) }
                loading={ jobsLoading || usersLoading }
                columnFilter={{
                  external: true,
                }}
                hover
                sorter={{
                  external: true,
                }}
                pagination={false}
                onRowClick={ handleRowClick }
                onColumnFilterChange={onColumnFilterChange}
                onSorterValueChange={onColumnSortChange}
                underTableSlot={ filteredJobs && filteredJobs.length > 0 && (
                  <>
                    <TableStats page={currentPage} total={totalTasks} />
                    <CPagination
                      className="ml-2"
                      onActivePageChange={(page) => setCurrentPage(page)}
                      activePage={currentPage} pages={Math.ceil(totalTasks/PAGE_SIZE)}
                    />
                  </>
                )}
                scopedSlots={{
                  'description': ({description}: TransformedJob) => (
                    <td>
                      <div className="line-clamp">
                        {description}
                      </div>
                    </td>
                  ),
                  'due_time': ({due_time}: TransformedJob) => (
                    <td className="due-time-column">
                      <div>
                        {due_time}
                      </div>
                    </td>
                  ),
                  'phase': ({ phase, job, completed, completedReasonCode }: TransformedJob) => {
                    return (
                      <td className="status-column">
                        <CBadge color={ JobPhaseColor[job.phase] || 'secondary'}>{ phase }</CBadge>
                        <br />
                        {completed && !!completedReasonCode && <div>{completedReasonCode}</div>}
                        <br />
                        <FeedbackRating rating={job?.feedback?.rating} containerClassName="mt-1 font-sm" />
                      </td>
                    );
                  },
                  'select': (transformedJob: TransformedJob) => {
                    const { id, job } = transformedJob;

                    if (![JobPhase.Scheduled, JobPhase.Pre, JobPhase.Live].includes(job.phase)) {
                      // eslint-disable-next-line jsx-a11y/control-has-associated-label
                      return <td className="checkbox-column" />;
                    }

                    const isChecked = bulkAction.selected.find(val => val._id === id) !== undefined;

                    return (
                      <td className="checkbox-column">
                        <CFormGroup variant="custom-checkbox" inline>
                          <CInputCheckbox
                            checked={ isChecked }
                            onChange={ () => {
                              if (isChecked) {
                                setBulkAction({
                                  ...bulkAction,
                                  selected: bulkAction.selected.filter(val => val._id !== id),
                                });
                              } else {
                                setBulkAction({
                                  ...bulkAction,
                                  selected: [...bulkAction.selected, job],
                                });
                              }
                            }}
                          />
                        </CFormGroup>
                      </td>
                    );
                  },
                  'actions': ({ job }: TransformedJob) => {
                    return (
                      <td>
                        <JobActions key={job?._id} job={ job } />
                      </td>
                    );
                  },
                  'details': ({ id, job }: TransformedJob) => {
                    if (showDrawer !== id) {
                      return null;
                    }

                    return (
                      <CCollapse show>
                        <JobsDetailsContainer job={job} />
                      </CCollapse>
                    );
                  }
                }}
              />
            </div>
          </CCard>
        </CCol>
      </CRow>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  search: state?.jobs?.search?.data,
  totalTasks: state?.jobs?.search?.total_tasks,
  jobsLoading: state?.jobs?.search?.isLoading,
  users: selectTechniciansIncludingRemoved(state),
  usersLoading: state?.userManagement?.users?.isLoading,
});

export default  connect(mapStateToProps)(JobsListPage);
